import { useState, useCallback, useEffect, useRef } from 'react';
import { USER_SETTINGS_DEFAULTS } from '../../../config/appConfig';

const STORAGE_KEY = 'historyDeleteConfirmation';

/**
 * Hook to manage history settings with localStorage persistence
 * Handles delete confirmation settings for each event type
 */
export function useHistorySettings() {
  // Initialize settings from localStorage with migration support
  const [deleteConfirmation, setDeleteConfirmation] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved !== null) {
      try {
        const value = JSON.parse(saved);
        // Handle migration from old boolean format to new object format
        if (typeof value === 'boolean') {
          return {
            match: value,
            assignment: value
          };
        }
        return value;
      } catch (e) {
        console.error('Failed to parse settings:', e);
      }
    }
    // Default confirmation settings
    return USER_SETTINGS_DEFAULTS.HISTORY.DELETE_CONFIRMATION;
  });

  // Auto-enable voice control setting
  const [autoEnableVoice, setAutoEnableVoice] = useState(() => {
    const saved = localStorage.getItem('historyAutoEnableVoice');
    return saved !== null ? JSON.parse(saved) : USER_SETTINGS_DEFAULTS.HISTORY.AUTO_ENABLE_VOICE;
  });

  // Stealth mode setting
  const [stealthMode, setStealthMode] = useState(() => {
    const saved = localStorage.getItem('stealthMode');
    return saved !== null ? JSON.parse(saved) : USER_SETTINGS_DEFAULTS.HISTORY.STEALTH_MODE;
  });

  // Keep a ref for stable access in callbacks
  const deleteConfirmationRef = useRef(deleteConfirmation);

  useEffect(() => {
    deleteConfirmationRef.current = deleteConfirmation;
  }, [deleteConfirmation]);

  /**
   * Update delete confirmation setting for a specific event type
   */
  const updateDeleteConfirmation = useCallback((eventType, value) => {
    setDeleteConfirmation(prev => {
      const newSettings = { ...prev, [eventType]: value };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
      return newSettings;
    });
  }, []);

  /**
   * Update auto-enable voice setting
   */
  const updateAutoEnableVoice = useCallback((value) => {
    setAutoEnableVoice(value);
    localStorage.setItem('historyAutoEnableVoice', JSON.stringify(value));
  }, []);

  /**
   * Update stealth mode setting
   */
  const updateStealthMode = useCallback((value) => {
    setStealthMode(value);
    localStorage.setItem('stealthMode', JSON.stringify(value));
  }, []);

  /**
   * Check if delete confirmation is required for a given event type
   */
  const shouldConfirmDelete = useCallback((eventType) => {
    return deleteConfirmationRef.current[eventType] || false;
  }, []);

  return {
    deleteConfirmation,
    deleteConfirmationRef,
    updateDeleteConfirmation,
    shouldConfirmDelete,
    autoEnableVoice,
    updateAutoEnableVoice,
    stealthMode,
    updateStealthMode
  };
}
